/**
 * Business inbox counts — approvals pending + dispatch queue (L3-NOTIF-v2)
 *
 * Polls active flow assignments; no websocket. Shared fetch for header strip + pages.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFlowApiClient } from '../../services/flowApiClient';
import { getCurrentUser } from '../../utils/jwtUtils';
import {
  parseFlowAssignmentRows,
  summarizeBusinessInboxCounts,
  type BusinessInboxCounts,
} from '../../utils/businessInboxSummary';

export const BUSINESS_INBOX_COUNTS_QUERY_KEY = 'business-inbox-counts';

const INBOX_POLL_MS = 60_000;

export interface UseBusinessInboxCountsOptions {
  enabled?: boolean;
}

export function useBusinessInboxCounts(
  tenantId: string,
  options?: UseBusinessInboxCountsOptions
): {
  counts: BusinessInboxCounts;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const enabled = options?.enabled ?? true;
  const userId = useMemo(() => getCurrentUser()?.userId ?? 'current-user', []);
  const assigneeEntityId = useMemo(() => getCurrentUser()?.entityId ?? userId, [userId]);
  const flowClient = useMemo(() => createFlowApiClient(tenantId), [tenantId]);

  const query = useQuery({
    queryKey: [BUSINESS_INBOX_COUNTS_QUERY_KEY, tenantId, userId, assigneeEntityId],
    enabled: enabled && Boolean(tenantId),
    queryFn: async () => {
      const assignmentsRes = await flowClient.getFlowsByAssignee(userId, assigneeEntityId, {
        status: 'active',
      });
      const rows = parseFlowAssignmentRows(assignmentsRes?.data ?? assignmentsRes);
      return summarizeBusinessInboxCounts(rows);
    },
    refetchInterval: INBOX_POLL_MS,
    staleTime: 30_000,
  });

  return {
    counts: query.data ?? { approvalPending: 0, dispatch: 0, total: 0 },
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
